import { mapGetters, mapActions, mapMutations } from 'vuex'

const MAGENTO_TYPE_DEFAULTS = {
  magefan: {
    postEndpoint: '/rest/V1/blog/post',
    putEndpoint: '/rest/V1/blog/post/{id}',
    authorId: '3',
    storeIds: '2'
  },
  mageplaza: {
    postEndpoint: '/rest/V1/mpblog/post/create',
    putEndpoint: '/rest/V1/mpblog/post/update/{id}',
    authorId: '1',
    storeIds: '0'
  }
}

const MAGENTO_TYPE_OPTIONS = [
  { text: 'Magefan Blog', value: 'magefan' },
  { text: 'Mageplaza Blog', value: 'mageplaza' }
]

export default {
  name: 'MagentoIntegration',
  data () {
    return {
      isLoading: false,
      isDeleting: false,
      magentoType: 'magefan',
      apiBaseUrl: '',
      apiUsername: '',
      apiPassword: '',
      postEndpoint: MAGENTO_TYPE_DEFAULTS.magefan.postEndpoint,
      putEndpoint: MAGENTO_TYPE_DEFAULTS.magefan.putEndpoint,
      authorId: MAGENTO_TYPE_DEFAULTS.magefan.authorId,
      storeIds: MAGENTO_TYPE_DEFAULTS.magefan.storeIds,
      rules: {
        required: value => !!value || 'Required.',
        validUrl: value => {
          const pattern = /^https?:\/\/.+/
          return pattern.test(value) || 'Please enter a valid URL starting with http:// or https://'
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'magentoIntegration'
    ]),
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    currentMagentoDefaults () {
      return MAGENTO_TYPE_DEFAULTS[this.magentoType] || MAGENTO_TYPE_DEFAULTS.magefan
    },
    magentoTypeOptions () {
      return MAGENTO_TYPE_OPTIONS
    },
    integrationTypeLabel () {
      return this.magentoType === 'mageplaza' ? 'Mageplaza Blog' : 'Magefan Blog'
    },
    permissionRequirement () {
      return this.magentoType === 'mageplaza' ? 'Mageplaza_Blog::blog' : 'Magefan_Blog::post_save'
    },
    activeIntegrationUrl () {
      return this.magentoIntegration?.magento_api_base_url
    },
    isValidToSave () {
      return this.isSupportedMagentoType(this.magentoType) &&
        !!this.apiBaseUrl &&
        !!this.apiUsername &&
        !!this.apiPassword &&
        !!this.postEndpoint &&
        !!this.putEndpoint &&
        !!this.authorId &&
        !!this.storeIds &&
        this.isValidUrl(this.apiBaseUrl) &&
        this.hasDomainOnlyBaseUrl(this.apiBaseUrl) &&
        this.isValidPostEndpoint(this.postEndpoint) &&
        this.isValidPutEndpoint(this.putEndpoint) &&
        this.isNumericId(this.authorId) &&
        this.isValidStoreIds(this.storeIds)
    }
  },
  methods: {
    ...mapActions([
      'getIntegrations',
      'postMagentoIntegration',
      'deleteIntegration'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    isValidUrl (url) {
      const pattern = /^https?:\/\/.+/
      return pattern.test(url)
    },
    isNumericId (value) {
      return /^\d+$/.test(String(value || '').trim())
    },
    isNumericIdList (value) {
      return /^\d+(\s*,\s*\d+)*$/.test(String(value || '').trim())
    },
    isValidStoreIds (value) {
      if (this.magentoType === 'mageplaza') return this.isNumericId(value)
      return this.isNumericIdList(value)
    },
    isValidPostEndpoint (value) {
      const endpoint = String(value || '').trim()
      if (this.magentoType === 'mageplaza') return /\/rest\/(?:[^/]+\/)?V1\/mpblog\/post\/create$/i.test(endpoint)
      return /\/blog\/post$/i.test(endpoint)
    },
    isValidPutEndpoint (value) {
      const endpoint = String(value || '').trim()
      if (this.magentoType === 'mageplaza') return /\/rest\/(?:[^/]+\/)?V1\/mpblog\/post\/update\/\{id\}$/i.test(endpoint)
      return /\/blog\/post\/\{id\}$/i.test(endpoint)
    },
    validPostEndpointRule (value) {
      if (this.isValidPostEndpoint(value)) return true
      if (this.magentoType === 'mageplaza') return 'Use /rest/V1/mpblog/post/create or /rest/<store_code>/V1/mpblog/post/create'
      return 'Use /rest/V1/blog/post'
    },
    validPutEndpointRule (value) {
      if (this.isValidPutEndpoint(value)) return true
      if (this.magentoType === 'mageplaza') return 'Use /rest/V1/mpblog/post/update/{id} or /rest/<store_code>/V1/mpblog/post/update/{id}'
      return 'Use /rest/V1/blog/post/{id}'
    },
    validStoreIdsRule (value) {
      if (this.isValidStoreIds(value)) return true
      if (this.magentoType === 'mageplaza') return 'Use one numeric store ID'
      return 'Use comma-separated numeric store IDs'
    },
    isSupportedMagentoType (value) {
      return Object.prototype.hasOwnProperty.call(MAGENTO_TYPE_DEFAULTS, value)
    },
    hasDomainOnlyBaseUrl (url) {
      try {
        const parsed = new URL(url.trim())
        return (!parsed.pathname || parsed.pathname === '/') && !parsed.search && !parsed.hash
      } catch (error) {
        return false
      }
    },
    applyMagentoTypeDefaults (magentoType) {
      const defaults = MAGENTO_TYPE_DEFAULTS[magentoType] || MAGENTO_TYPE_DEFAULTS.magefan
      this.postEndpoint = defaults.postEndpoint
      this.putEndpoint = defaults.putEndpoint
      this.authorId = defaults.authorId
      this.storeIds = defaults.storeIds
    },
    handleMagentoTypeChange (value) {
      if (!this.isSupportedMagentoType(value)) return
      this.magentoType = value
      this.applyMagentoTypeDefaults(value)
    },
    async saveIntegration () {
      if (!this.isValidToSave) return

      this.isLoading = true
      try {
        await this.postMagentoIntegration({
          contentplanId: this.contentplanId,
          magentoType: this.magentoType,
          apiBaseUrl: this.apiBaseUrl,
          apiUsername: this.apiUsername,
          apiPassword: this.apiPassword,
          postEndpoint: this.postEndpoint,
          putEndpoint: this.putEndpoint,
          authorId: this.authorId,
          storeIds: this.storeIds,
          type: 'magento'
        })

        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Magento integration saved!', type: 'success' })
      } catch (error) {
        console.error('Error saving Magento integration:', error)
        this.setSnackbar({
          open: true,
          text: `Error: ${error?.response?.data?.message || error.message || 'Could not save integration'}`,
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    },
    initializeFromIntegration (integration) {
      if (!integration || integration.type !== 'magento') return
      const magentoType = this.isSupportedMagentoType(integration.magento_type) ? integration.magento_type : 'magefan'
      const defaults = MAGENTO_TYPE_DEFAULTS[magentoType]
      this.magentoType = magentoType
      this.apiBaseUrl = integration.magento_api_base_url || ''
      this.apiUsername = integration.magento_api_username || ''
      this.apiPassword = ''
      this.postEndpoint = integration.magento_post_endpoint || defaults.postEndpoint
      this.putEndpoint = integration.magento_put_endpoint || defaults.putEndpoint
      this.authorId = integration.magento_author_id || defaults.authorId
      this.storeIds = integration.magento_store_ids || defaults.storeIds
    },
    async handleDelete () {
      if (!this.activeIntegrationUrl || this.isDeleting) return
      if (!window.confirm('are you sure you want to remove integration')) return

      this.isDeleting = true
      try {
        await this.deleteIntegration({
          contentplanId: this.contentplanId,
          integrationType: 'magento'
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Integration removed.', type: 'success' })
        this.$router.push({
          name: 'ContentplanSettings',
          params: { contentplanId: this.contentplanId, workspaceId: this.$route.params.workspaceId }
        })
      } catch (error) {
        this.setSnackbar({ open: true, text: `Error: ${error?.response?.data?.message || error.message || 'Could not remove integration'}`, type: 'error' })
      } finally {
        this.isDeleting = false
      }
    }
  },
  async mounted () {
    await this.getIntegrations({ contentplanId: this.contentplanId })
    this.initializeFromIntegration(this.magentoIntegration)
  }
}
